/**
 * Global back button that auto-mounts on every inner page.
 *
 * Smart-positions itself based on whether the page renders a sticky NavBar:
 *  - With NavBar: button sits just BELOW the navbar (~top:68px) so the
 *    navbar's logo / nav items stay clean.
 *  - Without NavBar (e.g. FoodLog, Water, Profile): button sits at the very
 *    top-left so it precedes the page's H1 cleanly.
 *
 * Also toggles a `has-back-button` and `has-navbar` body class so the
 * companion CSS rule in index.css can give the page `<main>` element the
 * right amount of top-padding without needing per-page edits.
 */
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';

const HIDDEN_PATHS = [
  '/',
  '/login',
  '/register',
  '/dashboard',
  '/landing',
  // NavBar is now rendered on /messages; its home icon replaces the pill.
  '/messages',
];

// Path prefixes (excluding the bare route itself) that suppress the global
// back button — used for inner pages that already render their own dedicated
// "Back to X" link in the page chrome.
const HIDDEN_PREFIXES = [
  '/challenges/',
  // DM thread pages already render their own custom header with a back
  // arrow next to the buddy's avatar; the yellow BACK pill would just
  // duplicate it right below the header.
  '/dm/',
  // Public profile pages render their own sticky header with a Back link
  // (and the app logo) — the global pill would overlap it awkwardly.
  '/user/',
  // Workout detail + live workout pages have their own header Back button.
  '/workout/',
  '/workouts/',
  '/live-workout/',
];

// Paths that should ALWAYS navigate to /dashboard when BACK is pressed,
// regardless of browser history. These are top-level nav destinations that
// users usually reach directly from the dashboard/wheel, so "back to home"
// matches their mental model. `/dm` intentionally uses `navigate(-1)`
// instead so it respects where the user came from — e.g. after tapping
// "Message" on someone's profile they expect Back to return to that
// profile, not skip all the way to the dashboard.
const FORCE_HOME_PATHS = [
  '/challenges',
  '/workouts',
  '/timer',
  '/leaderboard',
  '/social',
  '/exercises',
  '/food-log',
  '/profile',
  // `/messages` is a top-level destination (the inbox itself). Without this,
  // Back would try navigate(-1) which just re-enters the DM thread the user
  // just came from — an infinite bounce.
  '/messages',
];

export const GlobalBackButton = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [hasNavbar, setHasNavbar] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  const hidden =
    HIDDEN_PATHS.some(
      (p) => location.pathname === p || (p !== '/' && location.pathname === p + '/')
    ) ||
    HIDDEN_PREFIXES.some((p) => location.pathname.startsWith(p));

  // Detect both the sticky NavBar AND any full-screen modal overlay so the
  // back button auto-hides when something else owns the screen (e.g. the
  // conversation thread modal on /messages, the live-video overlay, etc.).
  useEffect(() => {
    if (hidden) {
      setHasNavbar(false);
      setModalOpen(false);
      return;
    }
    const check = () => {
      setHasNavbar(!!document.querySelector('header.sticky.top-0'));
      // A full-screen overlay = positioned `fixed inset-0` with z-index ≥ 50.
      const overlays = document.querySelectorAll('.fixed.inset-0');
      let modal = false;
      overlays.forEach((el) => {
        // Ignore the back button itself or invisible elements
        if (el.getAttribute('data-testid') === 'global-back-button') return;
        const z = parseInt(window.getComputedStyle(el).zIndex, 10);
        if (!Number.isNaN(z) && z >= 50) modal = true;
      });
      setModalOpen(modal);
    };
    check();
    const observer = new MutationObserver(check);
    observer.observe(document.body, { childList: true, subtree: true, attributes: true });
    // Keep observing — admin pages and modals may mount/unmount at any time.
    return () => {
      observer.disconnect();
    };
  }, [hidden, location.pathname]);

  // Body classes drive CSS padding adjustments on <main>.
  useEffect(() => {
    if (hidden) {
      document.body.classList.remove('has-back-button', 'has-navbar');
      return;
    }
    document.body.classList.add('has-back-button');
    document.body.classList.toggle('has-navbar', hasNavbar);
    return () => {
      document.body.classList.remove('has-back-button', 'has-navbar');
    };
  }, [hidden, hasNavbar]);

  if (hidden || modalOpen) return null;

  // Position: below navbar if present (so it doesn't clash with logo), else
  // tucked into the top-left corner of the page.
  const positionClass = hasNavbar ? 'top-[78px] left-3' : 'top-3 left-3';

  return (
    <button
      type="button"
      onClick={() => {
        // Top-level inner pages always go to /dashboard so users don't loop
        // back into a deeper screen they were just at (e.g. /challenges/:id).
        if (FORCE_HOME_PATHS.includes(location.pathname)) {
          navigate('/dashboard');
          return;
        }
        // If there's no meaningful history (direct link / shared URL),
        // fall back to /dashboard rather than dumping the user out of
        // the app entirely.
        if (window.history.length <= 1) {
          navigate('/dashboard');
        } else {
          navigate(-1);
        }
      }}
      aria-label="Go back"
      data-testid="global-back-button"
      className={`fixed ${positionClass} z-[60] group inline-flex items-center gap-1.5 pl-2.5 pr-3.5 py-2 rounded-full text-white transition-all
        bg-gradient-to-b from-card to-card/70 border border-white/15
        shadow-[inset_0_1px_0_rgba(255,255,255,0.08),0_4px_12px_rgba(0,0,0,0.4)]
        hover:border-primary/60 hover:text-primary hover:shadow-[inset_0_1px_0_rgba(255,255,255,0.12),0_6px_18px_rgba(212,175,55,0.18)]
        active:scale-95`}
    >
      <ChevronLeft className="h-4 w-4 transition-transform group-hover:-translate-x-0.5" strokeWidth={2.5} />
      <span className="font-heading text-[11px] uppercase tracking-[0.18em]">Back</span>
    </button>
  );
};

export default GlobalBackButton;
